import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Breadcrumbs from '../../Common/Breadcrumbs';
import PageHeader from '../../Common/PageHeader';
import { ReactComponent as EditIcon } from '../../assets/images/Edit.svg';
import { ReactComponent as DeleteIcon } from '../../assets/images/Delete.svg';
import ShimmerLoader from '../../Common/ShimmerLoader';
import { DeleteModal } from '../../Common/DeleteModal';
import { listNewsArticles } from '../../api/newsArticle/listNewsArticles';
import { deleteNewsArticle } from '../../api/newsArticle/deleteNewsArticle';
import { NewsArticle } from '../../api/newsArticle/types';
import { getImageUrl } from '../../utils/imageUrl';

const PAGE_SIZE = 10;

const NewsArticleList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [newsList, setNewsList] = useState<NewsArticle[]>([]);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [deleteTarget, setDeleteTarget] = useState<NewsArticle | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const activeId = location.state?.activeId || null;
  const searchInputRef = useRef<HTMLInputElement>(null);

  const fetchNews = async (searchValue = search, pageValue = page) => {
    setLoading(true);
    try {
      const data = await listNewsArticles(searchValue, pageValue);
      setNewsList(data.paginatedResults || []);
      setTotalPages(data.totalPages || 1);
      setTotalCount(data.totalCount || 0);
    } catch (err) {
      setNewsList([]);
      setTotalPages(1);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      fetchNews(search, page);
    }, 400);
    return () => clearTimeout(delayDebounce);
  }, [search, page]);

  const handleAddNews = () => {
    navigate('/news-articles/add');
  };

  const handleView = (news: NewsArticle) => {
    navigate(`/news-articles/${news._id}`, { state: { activeId: news._id } });
  };

  const handleEdit = (news: NewsArticle) => {
    navigate(`/news-articles/${news._id}`, { state: { activeId: news._id, edit: true, news } });
  };

  const handleDelete = (news: NewsArticle) => {
    setDeleteTarget(news);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteNewsArticle(deleteTarget._id!);
      setShowDeleteModal(false);
      setDeleteTarget(null);
      fetchNews();
    } catch (err) {
      // Optionally show error
    } finally {
      setDeleting(false);
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const handleClearSearch = () => {
    setSearch('');
    setPage(1);
    searchInputRef.current?.focus();
  };

  if (loading) {
    return (
      <div className="container-fluid" style={{ marginTop: '20px' }}>
        <Breadcrumbs items={[{ label: 'News Articles', active: true }]} />
        <PageHeader
          breadcrumb={null}
          title="News Articles Management"
          addNewLabel="Add News Article"
          onAddNew={handleAddNews}
        />
        <div className="custom-table-responsive" style={{ paddingBottom: 32 }}>
          <div className="filter-row">
            <div className="filter-controls">
              <div style={{ width: '240px', height: '38px' }} className="shimmer-base" />
            </div>
          </div>
          <ShimmerLoader type="table" rows={5} />
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid" style={{ marginTop: '20px' }}>
      <Breadcrumbs items={[{ label: 'News Articles', active: true }]} />
      <PageHeader
        breadcrumb={null}
        title="News Articles Management"
        addNewLabel="Add News Article"
        onAddNew={handleAddNews}
      />
      <div className="custom-table-responsive" style={{ paddingBottom: 32 }}>
        <div className="filter-row">
          <div className="filter-controls" style={{ gap: 12 }}>
            <div style={{ position: 'relative', width: 240 }}>
              <input
                type="text"
                className="form-search"
                placeholder="Search by title"
                style={{ width: 240, paddingRight: 32 }}
                value={search}
                onChange={handleSearchChange}
                ref={searchInputRef}
              />
              {search && (
                <button
                  type="button"
                  onClick={handleClearSearch}
                  style={{
                    position: 'absolute',
                    right: 8,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: 0,
                    zIndex: 2
                  }}
                  aria-label="Clear search"
                >
                  <span style={{ fontSize: 18, color: '#888' }}>&times;</span>
                </button>
              )}
            </div>
          </div>
        </div>
        <table className="custom-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Title</th>
              <th>Company Name</th>
              <th>Uploaded Date</th>
              <th>News Image</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {newsList.map((news, idx) => (
              <tr
                key={news._id}
                className={`clickable-row${activeId === news._id ? ' active-row' : ''}`}
                style={{ cursor: 'pointer', background: activeId === news._id ? '#eaf6ff' : undefined }}
                onClick={() => handleView(news)}
              >
                <td>{(page - 1) * PAGE_SIZE + idx + 1}</td>
                <td>{news.title}</td>
                <td>{news.companyName || '-'}</td>
                <td>{news.uploadedDate ? new Date(news.uploadedDate).toLocaleDateString() : '-'}</td>
                <td>
                  {news.image ? <img src={getImageUrl(news.image)} alt="news" style={{ width: 48, height: 32, objectFit: 'cover', borderRadius: 4 }} /> : '-'}
                </td>
                <td>
                  <span style={{ display: 'flex', gap: 8 }} onClick={e => e.stopPropagation()}>
                    <button className="icon-btn edit-btn" title="Edit" type="button" onClick={() => handleEdit(news)}><EditIcon /></button>
                    <button className="icon-btn delete-btn" title="Delete" type="button" onClick={() => handleDelete(news)}><DeleteIcon /></button>
                  </span>
                </td>
              </tr>
            ))}
            {newsList.length === 0 && (
              <tr>
                <td colSpan={6} className="text-center">No news articles found.</td>
              </tr>
            )}
          </tbody>
        </table>
        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div className="d-flex align-items-center justify-content-end mt-3 gap-2">
            <button className="btn btn-light" onClick={() => setPage(page - 1)} disabled={page === 1}>&lt;</button>
            <span>Page {page} of {totalPages}</span>
            <button className="btn btn-light" onClick={() => setPage(page + 1)} disabled={page === totalPages}>&gt;</button>
            <span className="ms-2">Total: {totalCount}</span>
          </div>
        )}
      </div>
      <DeleteModal
        show={showDeleteModal}
        handleClose={() => setShowDeleteModal(false)}
        deleteModalFunction={confirmDelete}
        title="Delete News Article"
        message={deleteTarget ? `Are you sure you want to delete '${deleteTarget.title}'? This action cannot be undone.` : ''}
        confirmLabel={deleting ? 'Deleting...' : 'Yes'}
        cancelLabel="No"
      />
    </div>
  );
};

export default NewsArticleList;
